import React, { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import { MobileContext } from './MobileContext';
import { useSessionStore } from './stores/sessionStore';

import Session from '@mythos/utils/Session';
import AllCardsPage from './AllCardsPage';
import { startChannelListener } from './ChannelListener';
import ConnectionBanner from './ConnectionBanner';
import GameHistoryListPage from './GameHistoryListPage';
import GamePage from './GamePage';
import LobbyListPage from './LobbyListPage';
import LobbyPage from './LobbyPage';
import LoginPage from './LoginPage';
import PageChrome from './PageChrome';
import RulesPage from './RulesPage';
import Toaster from './Toaster';
import UserProfilePage from './UserProfilePage';

import '../assets/css/style.css';
import AdminPage from './AdminPage';
import DevBuildOverlay from './DevBuildOverlay';
import DevLandingPage from './DevLandingPage';
import LeaderboardPage from './LeaderboardPage';
import LocalRoutes from './LocalRoutes';
import UserListPage from './UserListPage';

// Make FavIcon available for webpack to include in the bundle
export { default as FavIcon } from '../assets/favicon.png';

function MainPage() {
  const session = useSessionStore((state) => state.session);
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get('redirect');

  if (session && redirect) {
    return <Navigate to={redirect} replace />;
  }

  if (!session) {
    return <LoginPage redirect={redirect} />;
  }

  return <LobbyListPage />;
}

function PageLayoutRoute() {
  const session = useSessionStore((state) => state.session);
  return (
    <PageChrome session={session ?? undefined}>
      <Outlet />
    </PageChrome>
  );
}

function NotFoundHandler(props: {}) {
  return <p>Page not found</p>;
}

function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <StrictMode>
      <MobileContext.Provider value={isMobile}>
        <ConnectionBanner />
        <Toaster />
        <DevBuildOverlay />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route
              element={
                <RequireSession>
                  <Outlet />
                </RequireSession>
              }
            >
              <Route path="/game/:gameID" element={<GamePage />} />
            </Route>
            <Route element={<PageLayoutRoute />}>
              <Route path="/" element={<MainPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/all-cards" element={<AllCardsPage />} />
              <Route path="/rules" element={<RulesPage />} />

              <Route
                element={
                  <RequireSession>
                    <Outlet />
                  </RequireSession>
                }
              >
                <Route path="/lobby/:lobbyID" element={<LobbyPage />} />
                <Route path="/lobby-list" element={<LobbyListPage />} />

                <Route path="/user/:userID" element={<UserProfilePage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
              </Route>

              <Route
                path="/dev"
                element={
                  <RequireDeveloper>
                    <Outlet />
                  </RequireDeveloper>
                }
              >
                <Route index element={<DevLandingPage />} />
                <Route path="admin" element={<AdminPage />} />
                <Route
                  path="game-history-list"
                  element={<GameHistoryListPage history={history} />}
                />
                <Route path="users" element={<UserListPage />} />
              </Route>
            </Route>

            {LocalRoutes()}

            <Route path="*" element={<NotFoundHandler />} />
          </Routes>
        </BrowserRouter>
      </MobileContext.Provider>
    </StrictMode>
  );
}

function RequireSession({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const session = useSessionStore((state) => state.session);

  if (!session) {
    return (
      <Navigate
        to={`/?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  return children;
}

function RequireDeveloper({ children }: { children: React.ReactNode }) {
  const session = useSessionStore((state) => state.session);

  if (
    process.env.NODE_ENV !== 'development' &&
    (!session || !session.getUser().isDev)
  ) {
    return <Navigate to="/" replace />;
  }

  return children;
}

window.onload = () => {
  const json_session = window['__session' as any];
  const session = json_session ? Session.fromJSON(json_session) : null;

  useSessionStore.getState().setSession(session);
  startChannelListener(session);

  let container = document.getElementById('content');
  if (!container) {
    throw new Error('No element with id "content"');
  }

  const root = createRoot(container);
  root.render(<App />);
};
